// ============================================================
// VIEWER PREFERENCES - one person's own view, and nobody else's
// business.
//
// These have never synced and never will: whether YOU want names on
// the map is not a table ruling. What changed in v8.61.0 is that they
// are reachable from two places at once - the map's own toolbar and
// Settings › Mine - and two `useState` copies of the same localStorage
// blob do not agree with each other.
//
// So: ONE module-level store, and every hook reads it through
// `useSyncExternalStore`. Any number of components, one truth, no
// provider to thread through the tree and no context for tests to
// arrange.
//
// The snapshot is CACHED and only replaced on a write. React compares
// snapshots by identity, and a fresh object per read is an infinite
// render loop rather than a subtle bug.
// ============================================================

const STORAGE_KEY = 'dw-log-overlay';

let cache = null;
const listeners = new Set();

/** Read the persisted preferences, tolerating absent or corrupt data. */
function read() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function write(prefs) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage full or unavailable - a preference is not worth an error
  }
}

export function getViewerPrefs() {
  if (cache === null) cache = read();
  return cache;
}

export function subscribeViewerPrefs(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Write one preference. A new snapshot object, so React sees the change. */
export function setViewerPref(key, value) {
  const next = { ...getViewerPrefs(), [key]: value };
  cache = next;
  write(next);
  listeners.forEach((l) => l());
}

/** Read one preference's raw stored value - `undefined` when never set. */
export function getViewerPref(key) {
  return getViewerPrefs()[key];
}

/**
 * Forget everything read so far.
 *
 * For tests, which write localStorage directly between cases and would
 * otherwise be served the first case's snapshot forever.
 */
export function resetViewerPrefs() {
  cache = null;
  listeners.forEach((l) => l());
}
